import { keysIn, isEmpty, keyBy } from 'lodash';
import queryString from 'query-string';
import { round10 } from './NumberUtils';

/**
 * Escapes a string to prepare it to be used in a regex
 * @param {String} str
 * @return {String}
 */
const escapeRegExp = (str) => str.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');

/**
 * Replaces multiple strings with multiple other strings
 * @param {string} str
 * @param {Object} mapObj
 * @param {Object} options
 * @param {Object} [options.caseSensitive=false]
 */
const replaceAll = (str, mapObj, options) => {
    if (isEmpty(str)) {
        return '';
    }

    const DEFAULT_OPTIONS = {
        caseSensitive: false
    };

    const formattedOptions = {
        ...DEFAULT_OPTIONS,
        ...options
    };

    const keysInObject = keysIn(mapObj);
    if (keysInObject.length <= 0) {
        return str;
    }

    // escape keys
    const escapedKeysInObject = keysInObject.map((regexStr) => escapeRegExp(regexStr));

    // build regexOptions
    const regexOptions = `g${formattedOptions.caseSensitive ? '' : 'i'}`;

    const re = new RegExp(escapedKeysInObject.join('|'), regexOptions);

    return str.replace(re, (matched) => {
        if (formattedOptions.caseSensitive) {
            return mapObj[matched];
        }
        return mapObj[matched.toLowerCase()];
    });
};

/**
 *
 * @param {String} url
 * @param {String} key
 * @param {String|number} value
 */
const addUrlParameter = (url, key, value) => {
    if (isEmpty(url)) {
        return '';
    }
    let urlWithParam = url;

    // remove the hash part before operating on the uri
    const hashIndex = url.indexOf('#');
    const hash = hashIndex === -1 ? '' : url.substr(hashIndex);
    urlWithParam = hashIndex === -1 ? urlWithParam : urlWithParam.substr(0, hashIndex);

    const re = new RegExp(`([?&])${key}=.*?(&|$)`, 'i');
    const separator = urlWithParam.indexOf('?') !== -1 ? '&' : '?';

    if (urlWithParam.match(re)) {
        urlWithParam = urlWithParam.replace(re, `$1${key}=${value}$2`);
    } else {
        urlWithParam = `${urlWithParam}${separator}${key}=${value}`;
    }
    return `${urlWithParam}${hash}`; // finally append the hash as well
};

/**
 *
 * @param {String} url Url to check and remove querystring parameter
 * @param {String} parameter Named query string parameter that will be removed if it exists
 * @return {String}
 */
const removeUrlParameter = (url, parameter) => {
    // prefer to use l.search if you have a location/link object
    const urlParts = url.split('?');
    if (urlParts.length < 2) {
        return url;
    }

    const prefix = `${encodeURIComponent(parameter)}=`;
    const pars = urlParts[1].split(/[&;]/g);

    // reverse iteration as may be destructive
    for (let i = pars.length - 1; i >= 0; i -= 1) {
        // idiom for string.startsWith
        if (pars[i].lastIndexOf(prefix, 0) !== -1) {
            pars.splice(i, 1);
        }
    }

    const updatedUrl = urlParts[0] + (pars.length > 0 ? `?${pars.join('&')}` : '');
    return updatedUrl;
};

/**
 *
 * @param {String} string
 * @return {String}
 */
const htmlDecode = (string) => {
    const replacementValues = {
        '&amp;': '&'
    };

    return replaceAll(string, replacementValues);
};

const behaviorsFormatter = (c) => c && c.join(',');
const behaviorsParser = (c) => (c && c.length > 0 ? c.split(',') : []);

const formatNumberToCurrency = (number) =>
    round10(number, -2).toLocaleString('en-us', { style: 'currency', currency: 'USD' });

/**
 *
 * @param params
 * @param baseUrl
 * @returns {String}
 */
const buildQueryString = (params, baseUrl) => {
    let urlToSend = baseUrl;

    if (params) {
        const localQueryObject = {};
        let itemValue;
        Object.keys(params).forEach((item) => {
            itemValue = params[item];
            if (Array.isArray(itemValue)) {
                localQueryObject[item] = itemValue.join(',');
            } else {
                localQueryObject[item] = itemValue;
            }
        });

        const newQuery = queryString.stringify(localQueryObject);

        if (newQuery.length > 0) {
            urlToSend += `?${newQuery}`;
        }
    }
    return urlToSend;
};

const buildFileDownloadLink = (file) => {
    if (!file || !(file && file.fileName)) {
        return null;
    }
    const { fileName } = file;
    const fileExtension = fileName.substring(fileName.lastIndexOf('.') + 1);
    const fileNameNoExtension = fileName.slice(0, fileName.length - fileExtension.length - 1);

    return `/filestore/${fileNameNoExtension}_${file.parentId}_${file.version}.${fileExtension}`;
};

/**
 * Replaces double quotes with single quotes and removes HTML markup
 * @param {string} string
 * @return {string}
 * @private
 */
const removeHTMLtagsAndDoubleQuotes = (string = '') => {
    const HTMLregex = /(<([^>]+)>)/gi;
    const doubleQuoteRegex = /(")/gi;
    const nbspRegex = /&nbsp;/gi;
    return string.replace(HTMLregex, '').replace(doubleQuoteRegex, "'").replace(nbspRegex, '');
};

const truncateStringIfNecessary = (string, lengthLimit) => {
    if (string.length <= lengthLimit) {
        return string;
    }
    return `${string.slice(0, lengthLimit)}...`;
};

const csvToArray = (text) => {
    let p = '';
    let row = [''];
    const ret = [row];
    let i = 0;
    let r = 0;
    let s = !0;
    text.split('').forEach((l) => {
        if (l === '"') {
            if (s && l === p) row[i] += l;
            s = !s;
        } else if (l === ',' && s) {
            row[(i += 1)] = '';
        } else if (l === '\n' && s) {
            if (p === '\r') row[i] = row[i].slice(0, -1);
            ret[(r += 1)] = [''];
            row = ret[r];
            i = 0;
        } else row[i] += l;
        p = l;
    });
    return ret.map((arr) => arr.map((item) => item.trim()));
};

const removeEndingPunctuation = (string) => {
    const lastCharacter = string[string.length - 1];
    if (/[?.!,;:]$/g.test(lastCharacter)) {
        return string.slice(0, -1);
    }

    return string;
};

const algoliaFilterStringBuilder = (filtersObject, defaultFilter, typeLabels = [], indexName) => {
    let result = '';

    Object.keys(filtersObject).forEach((filterName) => {
        const filter = filtersObject[filterName];
        let includeFilterString = '';
        let excludeFilterString = '';
        const filtersArray = Object.keys(filter);

        for (let i = 0; i < filtersArray.length; i += 1) {
            const subfilter = filtersArray[i];
            if (typeof filter === 'string') {
                // will take custom filter string in the case of complex range or comparison filter
                includeFilterString = `${filterName}:${filter}`;
                break;
            } else if (filter[subfilter].isApplied === true) {
                if (filter[subfilter].type === 'INCLUDE') {
                    if (filter[subfilter].isBoolean) {
                        includeFilterString += `${filterName}:${subfilter === 'true'}} OR`;
                    } else {
                        includeFilterString += `${filterName}:"${subfilter}" OR `;
                        if (filterName === 'type' && typeLabels.includes(subfilter)) {
                            includeFilterString += `typeLabel:"${subfilter}" OR `;
                        }
                    }
                } else if (filter[subfilter].type === 'EXCLUDE') {
                    if (filter[subfilter].isBoolean) {
                        excludeFilterString += `NOT ${filterName}:${subfilter === 'true'}} OR`;
                    } else {
                        excludeFilterString += `NOT ${filterName}:"${subfilter}" OR `;
                    }
                }
            }
        }

        if (includeFilterString.length) {
            if (typeof filter !== 'string') {
                includeFilterString = includeFilterString.slice(0, -4); // removing trailing 'OR'
            }

            result += `(${includeFilterString}) AND `;
        }

        if (excludeFilterString.length) {
            if (typeof filter !== 'string') {
                excludeFilterString = excludeFilterString.slice(0, -4); // removing trailing 'OR'
            }
            result += `(${excludeFilterString}) AND `;
        }
    });
    const filterToReturn = defaultFilter ? result + defaultFilter : result.slice(0, -5);

    return defaultFilter ? result + defaultFilter : result.slice(0, -5); // removing trailing 'AND'
};

const createContentFilterString = (licenses) => {
    if (!licenses || !licenses.length) {
        return '';
    }
    return licenses.reduce((acc, item, i) => {
        let newAcc;
        if (i !== licenses.length - 1) {
            newAcc = `${acc}_id:${item.collectionId} OR `;
        } else {
            newAcc = `${acc}_id:${item.collectionId}`;
        }
        return newAcc;
    }, '');
};

const checkStringForBannedCharacters = (name = '', bannedCharacters = []) => {
    const bannedCharactersDict = keyBy(bannedCharacters);
    if (bannedCharactersDict[' ']) {
        bannedCharactersDict[' '] = 'space';
    }
    const faultyChars = {};
    for (let i = 0; i < name.length; i += 1) {
        const currentChar = name[i];
        if (bannedCharactersDict[currentChar]) {
            faultyChars[bannedCharactersDict[currentChar]] = bannedCharactersDict[currentChar];
        }
    }
    return {
        faultCount: Object.keys(faultyChars).length,
        faultyChars
    };
};

const emailIsValid = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const removeUrlSpaces = (key = '') => String(key ?? '').split(' ').join('%20');

const isValidId = (id) => {
    const checkForHexRegExp = new RegExp('^[0-9a-fA-F]{24}$');
    if (checkForHexRegExp.test(id)) {
        return true;
    }
    return false;
};

const titleCase = (str) =>
    str
        .toLowerCase()
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

const validateEmail = (email) =>
    email
        .toLowerCase()
        .match(
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        );

const validFileWithExpectedHeaders = (headers, fieldsToValidate) => {
    let fileValid = true;
    const errors = {};
    const errorDetail = 'Missing Column Header';

    Object.keys(fieldsToValidate).forEach((field) => {
        if (fieldsToValidate[field].required) {
            if (!headers.includes(field)) {
                fileValid = false;
                if (errors[errorDetail]) {
                    errors[errorDetail].errors.push(fieldsToValidate[field].column);
                } else {
                    errors[errorDetail] = {
                        type: 'Column(s)',
                        errors: [fieldsToValidate[field].column]
                    };
                }
            }
        }
    });

    return {
        fileValid,
        errors
    };
};

const validFileWithExpectedRow = (rowObj, headers, rowIdx, fieldsToValidate) => {
    let fileValid = true;
    const errors = {};

    if (isEmpty(rowObj)) {
        return {
            fileValid,
            errors
        };
    }

    // check row for valid values
    headers.forEach((header) => {
        const value = rowObj[header];
        if (header && fieldsToValidate[header]) {
            errors[fieldsToValidate[header].invalidValueError] = [];
        }

        const validateObj = fieldsToValidate[header];
        if (validateObj) {
            if (
                (validateObj.required && (!value || !value.length)) ||
                (validateObj.validate && value && !validateObj.validate(value))
            ) {
                errors[fieldsToValidate[header].invalidValueError].push(rowIdx + 2);
                fileValid = false;
            }
        }
    });

    return {
        fileValid,
        errors
    };
};

const colName = (num) => {
    let number = num;
    const ordA = 'a'.charCodeAt(0);
    const ordZ = 'z'.charCodeAt(0);
    const len = ordZ - ordA + 1;

    let str = '';

    while (number >= 0) {
        str = String.fromCharCode((number % len) + ordA) + str;
        number = Math.floor(number / len) - 1;
    }
    return str.toLocaleUpperCase();
};

export {
    csvToArray,
    createContentFilterString,
    replaceAll,
    addUrlParameter,
    removeUrlParameter,
    htmlDecode,
    escapeRegExp,
    behaviorsFormatter,
    behaviorsParser,
    formatNumberToCurrency,
    buildQueryString,
    buildFileDownloadLink,
    removeHTMLtagsAndDoubleQuotes,
    truncateStringIfNecessary,
    removeEndingPunctuation,
    algoliaFilterStringBuilder,
    emailIsValid,
    removeUrlSpaces,
    checkStringForBannedCharacters,
    isValidId,
    titleCase,
    validateEmail,
    validFileWithExpectedHeaders,
    validFileWithExpectedRow,
    colName
};
